
import { useState, useEffect, useRef } from "react";
import { Movie } from "@/types/movie";
import { getUserMovies } from "@/services/movieDatabase";
import { initialMovies } from "@/data/initialMovies";
import { logNetwork } from "@/utils/debugLogger";
import { useAuth } from "@/context/AuthContext";

/**
 * Simplified hook for handling movie data loading without auto-refresh
 */
export const useMovieLoading = () => {
  const [allMovies, setAllMovies] = useState<Movie[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user, loading: authLoading } = useAuth();
  
  // Track loading state to prevent multiple simultaneous loads
  const isLoadingRef = useRef(false);
  const loadAttempts = useRef(0);

  // Main effect for loading movies - simplified to only load on auth changes
  useEffect(() => {
    const loadMovies = async () => {
      loadAttempts.current += 1;
      const attemptNumber = loadAttempts.current;
      
      console.log(`🔄 [MOVIE-LOADING] Load attempt #${attemptNumber} starting:`, {
        authLoading,
        hasUser: !!user,
        userId: user?.id || 'none',
        isLoadingRef: isLoadingRef.current,
        currentMovieCount: allMovies.length
      });

      // Prevent multiple simultaneous loads
      if (isLoadingRef.current) {
        console.log(`🔄 [MOVIE-LOADING] Attempt #${attemptNumber} - Load already in progress, skipping`);
        return;
      }

      try {
        isLoadingRef.current = true;
        setIsLoading(true);
        
        // Wait for auth to finish loading
        if (authLoading) {
          console.log(`🔄 [MOVIE-LOADING] Attempt #${attemptNumber} - Waiting for authentication to complete...`);
          return;
        }
        
        console.log(`🔄 [MOVIE-LOADING] Attempt #${attemptNumber} - Auth ready, proceeding with data load`);
        
        if (user) {
          console.log(`✅ [MOVIE-LOADING] Attempt #${attemptNumber} - Loading movies for authenticated user:`, {
            userId: user.id,
            email: user.email
          });
          
          try {
            const movies = await getUserMovies();
            
            console.log(`📊 [MOVIE-LOADING] Attempt #${attemptNumber} - Data loaded successfully:`, {
              movieCount: movies.length,
              favoriteCount: movies.filter(m => m.favorite).length,
              planToWatchCount: movies.filter(m => m.planToWatch).length,
              sampleTitles: movies.slice(0, 3).map(m => m.title)
            });
            
            setAllMovies(movies);
            logNetwork(`Load attempt #${attemptNumber}: Loaded ${movies.length} movies for authenticated user`);
            
          } catch (loadError) {
            console.warn(`⚠️ [MOVIE-LOADING] Attempt #${attemptNumber} - Loading failed:`, loadError);
            
            // Don't clear existing movies on error, just log it
            if (allMovies.length === 0) {
              console.log(`🔄 [MOVIE-LOADING] Attempt #${attemptNumber} - No existing movies, using sample data`);
              setAllMovies(initialMovies);
            } else {
              console.log(`🔄 [MOVIE-LOADING] Attempt #${attemptNumber} - Keeping existing ${allMovies.length} movies`);
            }
          }
        } else {
          console.log(`👤 [MOVIE-LOADING] Attempt #${attemptNumber} - No authenticated user, showing sample data`);
          setAllMovies(initialMovies);
          logNetwork(`Load attempt #${attemptNumber}: Showing sample data for unauthenticated user`);
        }
      } catch (error) {
        console.error(`❌ [MOVIE-LOADING] Attempt #${attemptNumber} - Failed to load movies:`, {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          existingMovieCount: allMovies.length
        });
        
        // Don't clear existing movies on error, just log it
        if (allMovies.length === 0) {
          console.log(`🔄 [MOVIE-LOADING] Attempt #${attemptNumber} - No existing movies, using sample data as fallback`);
          setAllMovies(initialMovies);
        }
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
        console.log(`✅ [MOVIE-LOADING] Attempt #${attemptNumber} - Loading completed`);
      }
    };

    loadMovies();
  }, [authLoading, user]); // Removed session dependency to prevent unnecessary reloads

  // Manual refetch function - only for explicit user actions
  const refetchMovies = async () => {
    console.log(`🔄 [MOVIE-LOADING] Manual refetch requested:`, {
      authLoading,
      hasUser: !!user,
      isLoading: isLoadingRef.current,
      currentMovieCount: allMovies.length
    });
    
    if (authLoading) {
      console.log("⚠️ [MOVIE-LOADING] Cannot refetch while auth is loading");
      return;
    }
    
    if (isLoadingRef.current) {
      console.log("⚠️ [MOVIE-LOADING] Cannot refetch while load is in progress");
      return;
    }
    
    if (user) {
      try {
        isLoadingRef.current = true;
        setIsLoading(true);
        
        console.log(`🔄 [MOVIE-LOADING] Executing manual refetch for user: ${user.id}`);
        
        try {
          const movies = await getUserMovies();
          
          console.log(`✅ [MOVIE-LOADING] Manual refetch successful:`, {
            movieCount: movies.length,
            changed: movies.length !== allMovies.length
          });
          
          setAllMovies(movies);
          logNetwork(`Manual refetch: Loaded ${movies.length} movies for authenticated user`);
          
        } catch (fetchError) {
          console.warn("⚠️ [MOVIE-LOADING] Manual refetch failed, keeping existing data:", fetchError);
        }
      } catch (error) {
        console.error("❌ [MOVIE-LOADING] Failed to refetch movies:", error);
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    } else {
      console.log("⚠️ [MOVIE-LOADING] Cannot refetch without authenticated user");
    }
  };

  return {
    allMovies,
    setAllMovies,
    isLoading,
    isAuthenticated: !!user,
    refetchMovies
  };
};
